import React, { useState, useEffect, useRef, useCallback } from 'react';
import { apiClient, IS_NATIVE, API_URL } from '../utils/http';
import { Bell, X, Check, Flame, Trophy, Users, Gift, TrendingUp, Volume2, VolumeX, CreditCard, AlertTriangle } from 'lucide-react';
import { useAuth } from '../App';

const API = IS_NATIVE ? `${API_URL}/api` : '/api';

// Fonction pour mettre à jour le badge de l'app (PWA)
const updateAppBadge = async (count) => {
  try {
    if ('setAppBadge' in navigator) {
      if (count > 0) {
        await navigator.setAppBadge(count);
      } else {
        await navigator.clearAppBadge();
      }
    }
  } catch (error) {
  }
};

const NotificationCenter = ({ user, onNotificationUpdate }) => {
  const { updateUser } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [hasNewNotification, setHasNewNotification] = useState(false);
  const [latestNotification, setLatestNotification] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const panelRef = useRef(null);
  const buttonRef = useRef(null);
  const previousUnreadCount = useRef(0);
  const audioRef = useRef(null);

  // Créer l'élément audio pour le son de notification
  useEffect(() => {
    // Son de notification mélodieux (ding dong)
    // Utilisation de l'API Web Audio pour un son plus agréable
    const createNotificationSound = () => {
      try {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        
        const playTone = (frequency, startTime, duration) => {
          const oscillator = audioContext.createOscillator();
          const gainNode = audioContext.createGain();
          
          oscillator.connect(gainNode);
          gainNode.connect(audioContext.destination);
          
          oscillator.frequency.value = frequency;
          oscillator.type = 'sine';
          
          gainNode.gain.setValueAtTime(0, startTime);
          gainNode.gain.linearRampToValueAtTime(0.5, startTime + 0.02);
          gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
          
          oscillator.start(startTime);
          oscillator.stop(startTime + duration);
        };
        
        // Jouer deux notes harmonieuses (ding-dong)
        const now = audioContext.currentTime;
        playTone(880, now, 0.15); // La5
        playTone(659, now + 0.15, 0.2); // Mi5
        
        // Fermer le contexte après le son
        setTimeout(() => audioContext.close(), 500);
      } catch (e) {
      }
    };
    
    // Stocker la fonction pour l'utiliser plus tard
    audioRef.current = { play: createNotificationSound };
  }, []);

  // Jouer le son de notification
  const playNotificationSound = useCallback(() => {
    if (soundEnabled && audioRef.current && audioRef.current.play) {
      try {
        audioRef.current.play();
      } catch (e) {
      }
    }
    // Vibration sur mobile (pattern: vibrer, pause, vibrer)
    if ('vibrate' in navigator) {
      navigator.vibrate([150, 50, 150]);
    }
  }, [soundEnabled]);

  // Polling pour vérifier les nouvelles notifications toutes les 30 secondes
  useEffect(() => {
    if (!user) return;

    const checkForNewNotifications = async () => {
      try {
        const response = await apiClient.get(`${API}/notifications`);
          
        
        
        if (response.ok) {
          const data = await response.json();
          const newUnreadCount = data.unread_count || 0;
          
          // Détecter s'il y a de nouvelles notifications
          if (newUnreadCount > previousUnreadCount.current && previousUnreadCount.current !== 0) {
            // Nouvelle notification !
            setHasNewNotification(true);
            playNotificationSound();
            
            // Afficher le toast avec la dernière notification
            if (data.notifications && data.notifications.length > 0) {
              const newest = data.notifications.find(n => !n.read);
              if (newest) {
                setLatestNotification(newest);
                setShowToast(true);
                
                // Masquer le toast après 5 secondes
                setTimeout(() => {
                  setShowToast(false);
                }, 5000);
              }
            }
            
            // Arrêter l'animation après 3 secondes
            setTimeout(() => {
              setHasNewNotification(false);
            }, 3000);
          }
          
          previousUnreadCount.current = newUnreadCount;
          setUnreadCount(newUnreadCount);
          
          // Mettre à jour le badge de l'app (PWA)
          updateAppBadge(newUnreadCount);
        }
      } catch (error) {
        console.error('Erreur lors de la vérification des notifications:', error);
      }
    };

    // Vérification initiale
    checkForNewNotifications();
    
    // Polling toutes les 15 secondes
    const intervalId = setInterval(checkForNewNotifications, 15000);
    
    return () => clearInterval(intervalId);
  }, [user, playNotificationSound]);

  // Initialiser le compteur UNIQUEMENT au premier chargement
  // Après ça, l'API /notifications est la source de vérité
  const initializedRef = useRef(false);
  
  useEffect(() => {
    if (user && !initializedRef.current) {
      initializedRef.current = true;
      const count = user.notifications_count || 0;
      previousUnreadCount.current = count;
      setUnreadCount(count);
      updateAppBadge(count);
    }
    
    if (showNotifications && user) {
      fetchNotifications();
    }
  }, [user, showNotifications]);

  // Fermer le panneau quand on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showNotifications &&
        panelRef.current &&
        buttonRef.current &&
        !panelRef.current.contains(event.target) &&
        !buttonRef.current.contains(event.target)
      ) {
        setShowNotifications(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape' && showNotifications) {
        setShowNotifications(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showNotifications]);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(`${API}/notifications?limit=20`);
      if (response.data) {
        setNotifications(response.data.notifications || []);
        setUnreadCount(response.data.unread_count || 0);
        previousUnreadCount.current = response.data.unread_count || 0;
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = useCallback(async (notificationId, event) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    
    try {
      await apiClient.post(`${API}/notifications/${notificationId}/read`);
      setNotifications(prev => 
        prev.map(notif => 
          notif.id === notificationId ? { ...notif, read: true } : notif
        )
      );
      
      const newCount = Math.max(0, unreadCount - 1);
      setUnreadCount(newCount);
      previousUnreadCount.current = newCount;
      
      // 🔔 FIX: Update user context to sync badge on Dashboard/Viral
      if (updateUser) {
        updateUser({ notifications_count: newCount });
      }
      
      if (onNotificationUpdate) {
        onNotificationUpdate();
      }
    } catch (error) {
      console.error('Erreur lors du marquage comme lu:', error);
    }
  }, [onNotificationUpdate, unreadCount, updateUser]);

  const markAllAsRead = useCallback(async (event) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    
    try {
      await apiClient.post(`${API}/notifications/read-all`);
      // Mark all notifications as read in local state
      setNotifications(prev => prev.map(notif => ({ ...notif, read: true })));
      setUnreadCount(0);
      previousUnreadCount.current = 0;
      
      // 🔔 FIX: Update user context to sync badge on Dashboard/Viral
      if (updateUser) {
        updateUser({ notifications_count: 0 });
      }
      
      // Effacer le badge de l'app (PWA)
      updateAppBadge(0);
      
      if (onNotificationUpdate) {
        onNotificationUpdate();
      }
    } catch (error) {
      console.error('Erreur lors du marquage de toutes comme lues:', error);
    }
  }, [onNotificationUpdate, updateUser]);

  const togglePanel = useCallback((event) => {
    event.stopPropagation();
    event.preventDefault();
    setShowNotifications(prev => !prev);
    setHasNewNotification(false);
  }, []);

  const closePanel = useCallback((event) => {
    event.stopPropagation();
    event.preventDefault();
    setShowNotifications(false);
  }, []);

  const dismissToast = useCallback(() => {
    setShowToast(false);
  }, []);

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'streak':
        return <Flame className="w-5 h-5 text-orange-500" />;
      case 'level_up':
        return <Trophy className="w-5 h-5 text-yellow-500" />;
      case 'referral':
        return <Users className="w-5 h-5 text-blue-500" />;
      case 'reward':
        return <Gift className="w-5 h-5 text-green-500" />;
      case 'ranking':
        return <TrendingUp className="w-5 h-5 text-purple-500" />;
      case 'badge_unlocked':
        return <Trophy className="w-5 h-5 text-amber-500" />;
      case 'admin':
        return <Bell className="w-5 h-5 text-red-500" />;
      // Nouveaux types pour notifications admin
      case 'new_user':
        return <Users className="w-5 h-5 text-green-500" />;
      case 'wash_pending_admin':
        return <Bell className="w-5 h-5 text-blue-500" />;
      case 'review_validation':
        return <Bell className="w-5 h-5 text-purple-500" />;
      case 'wash_approved':
        return <Check className="w-5 h-5 text-green-500" />;
      case 'wash_rejected':
        return <X className="w-5 h-5 text-red-500" />;
      case 'review_validated':
        return <Check className="w-5 h-5 text-green-500" />;
      case 'wash_pending':
        return <Bell className="w-5 h-5 text-yellow-500" />;
      case 'chat_message':
        return <Bell className="w-5 h-5 text-cyan-500" />;
      // Trésors et œufs
      case 'treasure_found':
        return <Gift className="w-5 h-5 text-amber-500" />;
      case 'egg_collected':
        return <Gift className="w-5 h-5 text-pink-500" />;
      // Cartes NAYAX
      case 'card_recharged':
        return <CreditCard className="w-5 h-5 text-green-500" />;
      case 'card_gift_wash':
        return <Gift className="w-5 h-5 text-amber-500" />;
      case 'card_low_balance':
        return <AlertTriangle className="w-5 h-5 text-amber-500" />;
      default:
        return <Bell className="w-5 h-5 text-gray-500" />;
    }
  };

  const getNotificationColor = (type, isRead) => {
    const baseColor = isRead ? 'opacity-60' : '';
    
    switch (type) {
      case 'streak':
        return `bg-orange-50 border-orange-200 ${baseColor}`;
      case 'level_up':
        return `bg-yellow-50 border-yellow-200 ${baseColor}`;
      case 'referral':
        return `bg-blue-50 border-blue-200 ${baseColor}`;
      case 'reward':
        return `bg-green-50 border-green-200 ${baseColor}`;
      case 'ranking':
        return `bg-purple-50 border-purple-200 ${baseColor}`;
      case 'admin':
        return `bg-red-50 border-red-200 ${baseColor}`;
      // Nouveaux types pour notifications admin
      case 'new_user':
        return `bg-green-50 border-green-200 ${baseColor}`;
      case 'wash_pending_admin':
        return `bg-blue-50 border-blue-200 ${baseColor}`;
      case 'review_validation':
        return `bg-purple-50 border-purple-200 ${baseColor}`;
      case 'wash_approved':
        return `bg-green-50 border-green-200 ${baseColor}`;
      case 'wash_rejected':
        return `bg-red-50 border-red-200 ${baseColor}`;
      case 'review_validated':
        return `bg-green-50 border-green-200 ${baseColor}`;
      case 'wash_pending':
        return `bg-yellow-50 border-yellow-200 ${baseColor}`;
      case 'chat_message':
        return `bg-cyan-50 border-cyan-200 ${baseColor}`;
      // Trésors et œufs
      case 'treasure_found':
        return `bg-amber-50 border-amber-200 ${baseColor}`;
      case 'egg_collected':
        return `bg-pink-50 border-pink-200 ${baseColor}`;
      // Cartes NAYAX
      case 'card_recharged':
        return `bg-green-50 border-green-200 ${baseColor}`;
      case 'card_gift_wash':
        return `bg-amber-50 border-amber-200 ${baseColor}`;
      case 'card_low_balance':
        return `bg-amber-50 border-amber-200 ${baseColor}`;
      default:
        return `bg-gray-50 border-gray-200 ${baseColor}`;
    }
  };

  const formatTimeAgo = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'À l\'instant';
    if (diffInMinutes < 60) return `Il y a ${diffInMinutes}min`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `Il y a ${diffInHours}h`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    return `Il y a ${diffInDays}j`;
  };

  return (
    <>
      {/* Toast de notification en haut de l'écran - CORRIGÉ pour meilleure visibilité */}
      {showToast && latestNotification && (
        <div 
          className="fixed top-4 left-1/2 transform -translate-x-1/2 z-[9999] animate-slide-down"
          style={{ paddingTop: 'env(safe-area-inset-top)' }}
        >
          <div 
            className="relative bg-gradient-to-r from-purple-700 via-indigo-700 to-purple-800 rounded-2xl shadow-2xl p-4 max-w-sm mx-4 cursor-pointer hover:scale-105 transition-all duration-300 border-2 border-purple-400/50"
            onClick={() => {
              dismissToast();
              setShowNotifications(true);
            }}
          >
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-12 h-12 bg-amber-500 rounded-xl flex items-center justify-center shadow-lg">
                <div className="text-white">
                  {getNotificationIcon(latestNotification.type)}
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-amber-300 text-xs uppercase tracking-wide font-bold">Nouvelle notification</span>
                  <span className="w-2 h-2 bg-green-400 rounded-full animate-ping"></span>
                </div>
                <p className="font-bold text-white text-base mt-1">{latestNotification.title}</p>
                <p className="text-purple-100 text-sm mt-1 line-clamp-2">{latestNotification.message}</p>
              </div>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  dismissToast();
                }}
                className="flex-shrink-0 text-white hover:text-amber-300 transition-colors p-1 hover:bg-white/20 rounded-full"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="relative">
        {/* Bouton de notification avec animations */}
        <button
          ref={buttonRef}
          onClick={togglePanel}
          className={`relative p-2 rounded-lg transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-purple-500 ${
            hasNewNotification 
              ? 'bg-purple-100 text-purple-600' 
              : 'text-white/80 hover:text-white hover:bg-white/10'
          }`}
          aria-label="Notifications"
          aria-expanded={showNotifications}
        >
          {/* Icône de la cloche avec animation */}
          <Bell 
            className={`w-6 h-6 transition-transform ${
              hasNewNotification ? 'animate-ring' : ''
            }`}
          />
          
          {/* Badge du compteur */}
          {unreadCount > 0 && (
            <span 
              className={`absolute -top-1 -right-1 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold shadow-lg ${
                hasNewNotification 
                  ? 'bg-gradient-to-r from-red-500 to-pink-500 animate-bounce' 
                  : 'bg-red-500 animate-pulse'
              }`}
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
          
          {/* Cercle d'effet de pulse quand nouvelle notification */}
          {hasNewNotification && (
            <span className="absolute inset-0 rounded-lg bg-purple-400 animate-ping opacity-75"></span>
          )}
        </button>

        {/* Overlay pour fermer en cliquant à côté sur mobile */}
        {showNotifications && (
          <div 
            className="fixed inset-0 bg-black/30 z-40 backdrop-blur-sm"
            onClick={closePanel}
          />
        )}

        {/* Panneau de notifications */}
        {showNotifications && (
          <div 
            ref={panelRef}
            className="fixed sm:absolute right-2 sm:right-0 top-16 sm:top-full sm:mt-2 w-[calc(100vw-1rem)] sm:w-96 max-w-md bg-white rounded-2xl shadow-2xl border border-gray-200 z-50 animate-slide-up overflow-hidden"
          >
            {/* Header avec dégradé */}
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Bell className="w-5 h-5 text-white" />
                  <h3 className="text-lg font-bold text-white">Notifications</h3>
                  {unreadCount > 0 && (
                    <span className="bg-white/20 text-white text-xs px-2 py-0.5 rounded-full">
                      {unreadCount} nouvelle{unreadCount > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {/* Bouton son */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSoundEnabled(!soundEnabled);
                    }}
                    className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                    title={soundEnabled ? 'Désactiver le son' : 'Activer le son'}
                  >
                    {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={closePanel}
                    className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                    aria-label="Fermer"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              
              {/* Bouton Reset visible */}
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="mt-2 w-full py-2 bg-white/20 hover:bg-white/30 text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  <Check className="w-4 h-4" />
                  Tout marquer comme lu ({unreadCount})
                </button>
              )}
            </div>

            {/* Liste des notifications */}
            <div className="max-h-80 sm:max-h-[60vh] overflow-y-auto">
              {loading ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-4 border-purple-500 border-t-transparent mx-auto"></div>
                  <p className="text-gray-500 text-sm mt-3">Chargement...</p>
                </div>
              ) : notifications.length === 0 ? (
                <div className="p-8 text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Bell className="w-8 h-8 text-gray-300" />
                  </div>
                  <p className="text-gray-500 font-medium">Aucune notification</p>
                  <p className="text-gray-400 text-sm mt-1">Vous êtes à jour !</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {notifications.map((notification, index) => (
                    <div
                      key={notification.id}
                      className={`p-4 hover:bg-gray-50 transition-all cursor-pointer ${
                        getNotificationColor(notification.type, notification.read)
                      } ${!notification.read ? 'border-l-4 border-l-purple-500' : ''}`}
                      onClick={(e) => !notification.read && markAsRead(notification.id, e)}
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      <div className="flex items-start space-x-3">
                        <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                          notification.read ? 'bg-gray-100' : 'bg-gradient-to-r from-purple-100 to-pink-100'
                        }`}>
                          {getNotificationIcon(notification.type)}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <h4 className={`text-sm font-semibold ${
                            notification.read ? 'text-gray-500' : 'text-gray-800'
                          }`}>
                            {notification.title}
                          </h4>
                          <p className={`text-xs mt-1 ${
                            notification.read ? 'text-gray-400' : 'text-gray-600'
                          }`}>
                            {notification.message}
                          </p>
                          <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                            <span className="w-1.5 h-1.5 bg-gray-300 rounded-full"></span>
                            {formatTimeAgo(notification.created_at)}
                          </p>
                        </div>
                        
                        {!notification.read && (
                          <button
                            onClick={(e) => markAsRead(notification.id, e)}
                            className="flex-shrink-0 p-2 text-purple-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                            title="Marquer comme lu"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      
                      {/* Données spécifiques */}
                      {notification.data && Object.keys(notification.data).length > 0 && (
                        <div className="mt-3 ml-13 pl-10">
                          {notification.type === 'streak' && notification.data.streak_count && (
                            <span className="text-xs text-orange-600 bg-orange-100 px-2 py-1 rounded-full inline-flex items-center gap-1">
                              🔥 Streak de {notification.data.streak_count} semaines
                            </span>
                          )}
                          
                          {notification.type === 'referral' && notification.data.bonus_points && (
                            <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded-full inline-flex items-center gap-1">
                              💰 +{notification.data.bonus_points} points
                            </span>
                          )}
                          
                          {notification.type === 'level_up' && notification.data.new_level && (
                            <span className="text-xs text-yellow-600 bg-yellow-100 px-2 py-1 rounded-full inline-flex items-center gap-1">
                              🏆 Niveau {notification.data.new_level}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            {notifications.length > 0 && notifications.some(n => !n.read) && (
              <div className="p-3 border-t border-gray-100 bg-gray-50">
                <button
                  onClick={markAllAsRead}
                  className="w-full text-sm text-purple-600 hover:text-purple-700 font-semibold py-2 hover:bg-purple-50 rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  <Check className="w-4 h-4" />
                  Tout marquer comme lu
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Styles d'animation personnalisés */}
      <style>{`
        @keyframes ring {
          0%, 100% { transform: rotate(0deg); }
          10%, 30%, 50%, 70%, 90% { transform: rotate(-10deg); }
          20%, 40%, 60%, 80% { transform: rotate(10deg); }
        }
        
        @keyframes slide-down {
          from {
            opacity: 0;
            transform: translate(-50%, -20px);
          }
          to {
            opacity: 1;
            transform: translate(-50%, 0);
          }
        }
        
        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-ring {
          animation: ring 0.5s ease-in-out infinite;
        }
        
        .animate-slide-down {
          animation: slide-down 0.3s ease-out forwards;
        }
        
        .animate-slide-up {
          animation: slide-up 0.2s ease-out forwards;
        }
      `}</style>
    </>
  );
};

export default NotificationCenter;
